import type { Appointment, Patient } from '@medplum/fhirtypes';
import {
  ActionIcon,
  Badge,
  Box,
  Divider,
  Group,
  Paper,
  Skeleton,
  Stack,
  Text,
  ThemeIcon,
  Tooltip,
} from '@mantine/core';
import { useState, useEffect, type ReactNode } from 'react';
import {
  IconBuilding,
  IconCalendarEvent,
  IconChevronLeft,
  IconChevronRight,
  IconClipboardList,
  IconNotes,
  IconReceipt,
  IconStethoscope,
  IconUsers,
  IconVideo,
} from '@tabler/icons-react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { notifications } from '@mantine/notifications';
import { shouldShowAppointmentOpenSession } from '@/features/calendar/calendarEventGuards';
import { InPersonRecordingConsentModal } from './components/InPersonRecordingConsentModal';
import { isZoomEnabled } from '@/lib/zoom/useZoomMeeting';
import {
  hasPriorInPersonRecording,
  isInPersonRecordingSessionActive,
} from '@/lib/zoom/inPersonRecordingService';
import { isInPersonRecordingRoute } from './InPersonRecordingPage';
import type { AppointmentSessionModality } from './utils/appointmentModality';
import {
  getAppointmentStatusBadgeColor,
  formatAppointmentStatus,
  COMPLETED_STATUSES,
} from './utils/appointmentStatus';
import { buildAppointmentDisplayTitle } from './utils/buildAppointmentDisplayTitle';
import { AppointmentTitleText } from './utils/AppointmentTitleText';
import type { ParticipantAttendanceFhirStatus } from './utils/participantAttendance';
import type { LinkedAppointmentInfo } from './types';
import { AppointmentActionsCluster } from './components/AppointmentActionsCluster';
import { SessionRecordingConsentBadge } from '../../components/SessionRecordingConsentBadge';
import {
  AppointmentServiceCodesTable,
  type AppointmentServiceCodeRow,
} from '@/components/AppointmentServiceCodesTable';
import {
  AppointmentParticipantsTable,
  type AppointmentParticipantRow,
} from '@/components/AppointmentParticipantsTable';
import { SectionHeader } from '@/components/SectionHeader';

function getPreferredName(patient: Patient): string {
  const preferred = patient.name?.find((n) => n.use === 'usual');
  if (preferred) {
    const given = preferred.given?.join(' ') ?? '';
    const family = preferred.family ?? '';
    return `${given} ${family}`.trim();
  }
  return formatName(patient);
}

function formatName(patient: Patient): string {
  const name = patient.name?.[0];
  if (!name) return 'Unknown';
  const given = name.given?.join(' ') ?? '';
  const family = name.family ?? '';
  return `${given} ${family}`.trim() || 'Unknown';
}

function formatAppointmentDate(dateStr: string): string {
  try {
    return new Date(dateStr).toLocaleDateString('en-US', {
      weekday: 'short',
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  } catch {
    return dateStr;
  }
}

function formatTimeRange(start: string, end?: string): { range: string; durationLabel?: string } {
  try {
    const startDate = new Date(start);
    const startStr = startDate.toLocaleTimeString(undefined, {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
    });
    if (!end) return { range: startStr };
    const endDate = new Date(end);
    const endStr = endDate.toLocaleTimeString(undefined, {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
    });
    const durationMin = Math.round((endDate.getTime() - startDate.getTime()) / 60000);
    return {
      range: `${startStr} – ${endStr}`,
      durationLabel: `${durationMin} min`,
    };
  } catch {
    return { range: start };
  }
}

function formatModalityLabel(modality?: AppointmentSessionModality): string {
  if (!modality) return '—';
  if (modality === 'in-person') return 'In person';
  if (modality === 'telehealth') return 'Telehealth';
  return 'Hybrid';
}

export interface PatientHeaderProps {
  patient: Patient | null;
  loading?: boolean;
  groupDisplayName?: string;
  episodeDisplayName?: string;
  providerDisplayName?: string;
  appointmentStart?: string;
  appointmentEnd?: string;
  sessionType?: string;
  attendanceStatus?: string;
  attendanceStatusLabel?: string;
  attendanceStatusBadgeColor?: string;
  sessionDurationMinutes?: number | null;
  previousAppointment?: LinkedAppointmentInfo | null;
  nextAppointment?: LinkedAppointmentInfo | null;
  videoMeetingLink?: string;
  appointmentId?: string;
  sessionModality?: AppointmentSessionModality;
  /** Binary recording-consent indicator; null/undefined hides it (loading or not applicable). */
  sessionRecordingConsented?: boolean | null;
  fhirAppointment?: Appointment;
  serviceCodeRows?: AppointmentServiceCodeRow[];
  participantRows?: AppointmentParticipantRow[];
  onEditAppointment?: () => void;
  /** Opens the in-person session-tools drawer (owned by AppointmentPage). */
  onOpenSessionTools?: () => void;
  /** Start in-person recording (opens COI host popup; owned by AppointmentPage). */
  onBeginInPersonRecording?: () => Promise<void>;
  /** Force-end recording session (owned by AppointmentPage). */
  onEndInPersonRecording?: () => void;
  recordingStarting?: boolean;
  recordingEnding?: boolean;
  recordingSetupPhase?: import('./components/InPersonRecordingConsentModal').InPersonRecordingModalPhase;
  recordingSetupError?: string | null;
  recordingHostJoined?: boolean;
  /** FHIR session-recording Consent on file for every Patient (null while loading). */
  recordingConsentOnFile?: boolean | null;
  onClearRecordingSetupError?: () => void;
  onAttendanceChange?: (patientId: string, status: ParticipantAttendanceFhirStatus) => void;
  savedStatusByPatientId?: Record<string, ParticipantAttendanceFhirStatus | undefined>;
  onLocationChange?: (patientId: string, location: string | undefined) => void;
  locationOptionsByPatientId?: Record<string, string[]>;
  /**
   * When false, hide Edit / Open / Copy / Record / Calendar in the header.
   * Used after the progress note is signed until Revise Note unlocks editing again.
   */
  showHeaderActions?: boolean;
  /** Copy meeting link is only for people on the appointment. */
  viewerIsParticipant?: boolean;
}

/**
 * Appointment page header: display title, metadata, stacked tables, visible actions.
 */
export function PatientHeader({
  patient,
  loading,
  groupDisplayName,
  episodeDisplayName,
  providerDisplayName,
  appointmentStart,
  appointmentEnd,
  sessionType,
  attendanceStatus,
  attendanceStatusLabel,
  attendanceStatusBadgeColor,
  sessionDurationMinutes,
  previousAppointment,
  nextAppointment,
  videoMeetingLink,
  appointmentId,
  sessionModality,
  sessionRecordingConsented,
  fhirAppointment,
  serviceCodeRows = [],
  participantRows = [],
  onEditAppointment,
  onOpenSessionTools,
  onBeginInPersonRecording,
  onEndInPersonRecording,
  recordingStarting = false,
  recordingEnding = false,
  recordingSetupPhase = 'consent',
  recordingSetupError = null,
  recordingHostJoined = false,
  recordingConsentOnFile = null,
  onClearRecordingSetupError,
  onAttendanceChange,
  savedStatusByPatientId,
  onLocationChange,
  locationOptionsByPatientId,
  showHeaderActions = true,
  viewerIsParticipant = false,
}: PatientHeaderProps): JSX.Element {
  const [recordingModalOpen, setRecordingModalOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  if (loading || (!patient && !groupDisplayName)) {
    return (
      <Paper p="lg" withBorder>
        <Group gap="sm">
          <Skeleton height={24} width={200} radius="sm" />
          <Skeleton height={16} width={300} radius="sm" />
        </Group>
      </Paper>
    );
  }

  const preferred = patient ? getPreferredName(patient) : groupDisplayName!;
  const notCompleted =
    !attendanceStatus || !COMPLETED_STATUSES.has(attendanceStatus.toLowerCase().replace(/\s+/g, '-'));
  const isInPersonSession = sessionModality === 'in-person';
  const showCopyLink = Boolean(videoMeetingLink) && sessionModality !== 'in-person' && viewerIsParticipant;
  const showOpenSession = shouldShowAppointmentOpenSession({
    isInPerson: isInPersonSession,
    isParticipant: viewerIsParticipant,
  });
  const recordingInProgress =
    isInPersonRecordingRoute(location.pathname) ||
    isInPersonRecordingSessionActive(fhirAppointment);
  // In-person recording requires FHIR session-recording Consent (same gate as
  // telehealth) plus verbal attestation in the start modal.
  // Hide Record after session is fulfilled/cancelled/noshow; keep Edit / Open /
  // Copy / Calendar so providers can still change location and navigate.
  const showRecordButton = isInPersonSession && isZoomEnabled() && notCompleted;
  const statusBadgeLabel = attendanceStatusLabel ?? formatAppointmentStatus(attendanceStatus);
  const statusBadgeColor = attendanceStatusBadgeColor ?? getAppointmentStatusBadgeColor(attendanceStatus);

  const timeParts = appointmentStart
    ? formatTimeRange(appointmentStart, appointmentEnd)
    : { range: '—', durationLabel: undefined };
  const durationDisplay =
    sessionDurationMinutes != null
      ? `${sessionDurationMinutes} min`
      : timeParts.durationLabel;
  const dateTimeDuration = [
    appointmentStart ? formatAppointmentDate(appointmentStart) : 'No date set',
    timeParts.range,
    durationDisplay,
  ]
    .filter(Boolean)
    .join(' · ');

  const title = buildAppointmentDisplayTitle({
    groupDisplayName,
    episodeDisplayName,
    clientDisplayName: patient ? preferred : undefined,
  });

  const handleCopyLink = async (): Promise<void> => {
    if (!videoMeetingLink) return;
    try {
      await navigator.clipboard.writeText(videoMeetingLink);
      notifications.show({
        color: 'green',
        message: 'Session link copied',
      });
    } catch {
      notifications.show({
        color: 'red',
        message: 'Could not copy session link',
      });
    }
  };

  const handleOpenSession = (): void => {
    if (!showOpenSession) return;
    if (isInPersonSession) {
      onOpenSessionTools?.();
      return;
    }
    if (appointmentId) {
      navigate(`/dashboard/session/${appointmentId}`);
    }
  };

  const handleStartRecording = (): void => {
    onClearRecordingSetupError?.();
    setRecordingModalOpen(true);
  };

  const handleConfirmRecording = (): void => {
    if (recordingConsentOnFile !== true) return;
    void onBeginInPersonRecording?.();
  };

  const handleCloseRecordingModal = (): void => {
    if (recordingStarting && recordingSetupPhase !== 'failed') return;
    onClearRecordingSetupError?.();
    setRecordingModalOpen(false);
  };

  useEffect(() => {
    if (recordingHostJoined && recordingModalOpen) {
      setRecordingModalOpen(false);
    }
  }, [recordingHostJoined, recordingModalOpen]);

  return (
    <Paper withBorder radius="md" style={{ overflow: 'hidden' }}>
      <Box bg="gray.0" px="lg" py="md">
        <Group justify="space-between" align="flex-start" wrap="nowrap" gap="md">
          <Group gap="md" align="center" wrap="nowrap" style={{ flex: 1, minWidth: 0 }}>
            <ThemeIcon variant="light" color="gold" radius="md" size={44}>
              <IconCalendarEvent size={22} />
            </ThemeIcon>
            <Stack gap={4} style={{ flex: 1, minWidth: 0 }}>
              <Group gap="sm" wrap="nowrap" align="center">
                <AppointmentTitleText
                  title={title}
                  clientDisplayName={patient ? preferred : undefined}
                  clientChartHref={patient?.id ? `/dashboard/clients/${patient.id}` : undefined}
                  fw={700}
                  size="xl"
                  style={{ lineHeight: 1.2 }}
                />
                {attendanceStatus ? (
                  <Badge color={statusBadgeColor} variant="light" size="sm">
                    {statusBadgeLabel}
                  </Badge>
                ) : null}
                {sessionRecordingConsented != null ? (
                  <SessionRecordingConsentBadge consented={sessionRecordingConsented} size="sm" />
                ) : null}
                <CompactPrevNext previous={previousAppointment} next={nextAppointment} />
              </Group>
              <Text size="sm" c="gray.7">
                {dateTimeDuration}
              </Text>
            </Stack>
          </Group>
          {showHeaderActions ? (
            <AppointmentActionsCluster
              onEdit={() => onEditAppointment?.()}
              onOpenSession={handleOpenSession}
              showOpenSession={showOpenSession}
              showCopyLink={showCopyLink}
              onCopyLink={showCopyLink ? () => void handleCopyLink() : undefined}
              showRecordButton={showRecordButton}
              onStartRecording={handleStartRecording}
              onEndRecording={onEndInPersonRecording}
              recordingInProgress={recordingInProgress}
              endingRecording={recordingEnding}
              onViewCalendar={() => {
                if (!appointmentId) {
                  navigate('/dashboard/calendar');
                  return;
                }
                navigate(`/dashboard/calendar?eventId=${encodeURIComponent(appointmentId)}`);
              }}
            />
          ) : null}
        </Group>
      </Box>

      <Divider />

      <Box px="lg" py="md">
        <Group gap="lg" align="stretch" wrap="nowrap">
          <Box flex={1} miw={0}>
            <MetaField
              icon={<IconStethoscope size={14} />}
              label="Provider"
              value={providerDisplayName || '—'}
            />
          </Box>
          <Divider orientation="vertical" />
          <Box flex={1} miw={0}>
            <MetaField
              icon={isInPersonSession ? <IconBuilding size={14} /> : <IconVideo size={14} />}
              label="Modality"
              value={formatModalityLabel(sessionModality)}
            />
          </Box>
          <Divider orientation="vertical" />
          <Box flex={1} miw={0}>
            <MetaField
              icon={<IconNotes size={14} />}
              label="Episode of care"
              value={episodeDisplayName || '—'}
            />
          </Box>
          <Divider orientation="vertical" />
          <Box flex={1} miw={0}>
            <MetaField
              icon={<IconClipboardList size={14} />}
              label="Session Type"
              value={sessionType || '—'}
            />
          </Box>
        </Group>
      </Box>

      <Divider />

      <Stack gap={0} px="lg" pb="lg" pt="md">
        <Box>
          <SectionHeader icon={<IconUsers size={16} />} label="Participants" />
          <Box mt="xs">
            <AppointmentParticipantsTable
              appearance="card"
              rows={participantRows}
              onAttendanceChange={onAttendanceChange}
              savedStatusByPatientId={savedStatusByPatientId}
              onLocationChange={onLocationChange}
              locationOptionsByPatientId={locationOptionsByPatientId}
            />
          </Box>
        </Box>

        <Divider my="sm" />

        <Box>
          <SectionHeader icon={<IconReceipt size={16} />} label="Service codes" />
          <Box mt="xs">
            <AppointmentServiceCodesTable appearance="card" rows={serviceCodeRows} />
          </Box>
        </Box>
      </Stack>

      {isInPersonSession ? (
        <InPersonRecordingConsentModal
          opened={recordingModalOpen}
          onClose={handleCloseRecordingModal}
          onConfirm={handleConfirmRecording}
          phase={recordingSetupPhase}
          errorMessage={recordingSetupError}
          hasPriorRecording={hasPriorInPersonRecording(fhirAppointment)}
          recordingConsentOnFile={recordingConsentOnFile}
        />
      ) : null}
    </Paper>
  );
}

/** Icon + label stacked above its value, per the appointment header metadata style. */
function MetaField({
  icon,
  label,
  value,
}: {
  icon: ReactNode;
  label: string;
  value: ReactNode;
}): JSX.Element {
  return (
    <Stack gap={4} style={{ minWidth: 0 }}>
      <Group gap={6} wrap="nowrap" c="dimmed">
        {icon}
        <Text size="xs" c="dimmed" fw={600}>
          {label}
        </Text>
      </Group>
      {typeof value === 'string' ? (
        <Text size="sm" c="gray.8" fw={500}>
          {value}
        </Text>
      ) : (
        <Box style={{ minWidth: 0 }}>{value}</Box>
      )}
    </Stack>
  );
}

function CompactPrevNext({
  previous,
  next,
}: {
  previous?: LinkedAppointmentInfo | null;
  next?: LinkedAppointmentInfo | null;
}): JSX.Element {
  return (
    <Group gap={2} wrap="nowrap">
      <Tooltip label={previous?.date ? `Previous: ${previous.date}` : 'No previous appointment'}>
        {previous?.id ? (
          <ActionIcon
            component={Link}
            to={`/dashboard/appointments/${previous.id}`}
            state={{ appointmentSequenceNumber: previous.sequenceNumber }}
            variant="subtle"
            color="gray"
            size="sm"
            aria-label="Previous appointment"
          >
            <IconChevronLeft size={16} />
          </ActionIcon>
        ) : (
          <ActionIcon variant="subtle" color="gray" size="sm" disabled aria-label="Previous appointment">
            <IconChevronLeft size={16} />
          </ActionIcon>
        )}
      </Tooltip>
      <Tooltip label={next?.date ? `Next: ${next.date}` : 'No next appointment'}>
        {next?.id ? (
          <ActionIcon
            component={Link}
            to={`/dashboard/appointments/${next.id}`}
            state={{ appointmentSequenceNumber: next.sequenceNumber }}
            variant="subtle"
            color="gray"
            size="sm"
            aria-label="Next appointment"
          >
            <IconChevronRight size={16} />
          </ActionIcon>
        ) : (
          <ActionIcon variant="subtle" color="gray" size="sm" disabled aria-label="Next appointment">
            <IconChevronRight size={16} />
          </ActionIcon>
        )}
      </Tooltip>
    </Group>
  );
}
